import { useState } from 'react';
import type { ReactNode } from 'react';
import { cx } from '@/lib/format';

// ── Section header ───────────────────────────────────────────────────────────

interface SectionHeaderProps {
  eyebrow: string;
  title: string;
  intro?: string;
  action?: ReactNode;
}

export function SectionHeader({ eyebrow, title, intro, action }: SectionHeaderProps) {
  return (
    <div className="mb-10 sm:mb-14">
      <div className="ticks mb-5" aria-hidden />
      <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <p className="eyebrow mb-3">{eyebrow}</p>
          <h2 className="h2 text-balance">{title}</h2>
          {intro && (
            <p className="mt-4 max-w-prose text-pretty leading-relaxed text-muted">{intro}</p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
}

// ── Lazy image ───────────────────────────────────────────────────────────────

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  aspect?: string;
  priority?: boolean;
}

export function LazyImage({
  src,
  alt,
  className,
  aspect = 'aspect-[16/10]',
  priority,
}: LazyImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <div className={cx('relative overflow-hidden bg-elevated', aspect, className)}>
      {!loaded && !failed && <div className="skeleton absolute inset-0" aria-hidden />}
      {failed ? (
        <div className="absolute inset-0 grid place-items-center">
          <span className="font-mono text-2xs uppercase tracking-[0.16em] text-faint">{alt}</span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={cx(
            'h-full w-full object-cover transition-opacity duration-500',
            loaded ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}
    </div>
  );
}

// ── Proficiency meter ────────────────────────────────────────────────────────

/** A bar plus an explicit "7/10": a self-assessed level is a judgement, and
 *  "70%" would dress it up as a measurement. */
export function LevelMeter({
  level,
  label,
  color,
}: {
  level: number;
  label: string;
  color?: string | null;
}) {
  return (
    <span className="inline-flex items-center gap-2" role="img" aria-label={`${label}: ${level}/10`}>
      <span className="h-1 w-20 overflow-hidden bg-line">
        <span
          className="block h-full transition-all duration-500 ease-smooth"
          style={{ width: `${level * 10}%`, backgroundColor: color ?? 'rgb(var(--c-accent))' }}
        />
      </span>
      <span className="w-9 shrink-0 text-right font-mono text-2xs tabular text-faint">
        {level}/10
      </span>
    </span>
  );
}

// ── Status pill ──────────────────────────────────────────────────────────────

export function StatusPill({ label, live = false }: { label: string; live?: boolean }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-2 border px-2.5 py-1.5 font-mono text-2xs uppercase tracking-[0.14em]',
        live ? 'border-accent/40 bg-accent/10 text-accent' : 'border-line bg-elevated text-muted'
      )}
    >
      {live && (
        <span className="relative flex h-1.5 w-1.5" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping bg-accent opacity-60" />
          <span className="relative inline-flex h-1.5 w-1.5 bg-accent" />
        </span>
      )}
      {label}
    </span>
  );
}

// ── Feedback states ──────────────────────────────────────────────────────────

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        'inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className
      )}
      role="status"
      aria-hidden
    />
  );
}

export function PageLoader({ label }: { label: string }) {
  return (
    <div className="grid min-h-[60vh] place-items-center">
      <div className="flex items-center gap-3 text-muted">
        <Spinner />
        <span className="font-mono text-2xs uppercase tracking-[0.16em]">{label}</span>
      </div>
    </div>
  );
}

export function ErrorState({
  message,
  action,
  actionLabel,
}: {
  message: string;
  action?: () => void;
  actionLabel?: string;
}) {
  return (
    <div className="grid min-h-[60vh] place-items-center px-5">
      <div className="text-center">
        <p className="text-muted">{message}</p>
        {action && actionLabel && (
          <button type="button" onClick={action} className="btn-outline mt-6">
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}
